import { AlertTriangle, Ban, Download, Loader2, Plus, ShieldAlert, ShieldCheck, Trash2, UserCog, VolumeX } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { EmptyState, PageLoading } from '../../components/Shells';
import { useApp } from '../../lib/app-context';
import { api, downloadCsv, formatDate } from '../../lib/api';

const guardLabels: Record<string, string> = {
  captcha_enabled: '入群驗證', antiflood_enabled: '防洗版', link_guard_enabled: '連結守衛',
  forward_guard_enabled: '禁止轉發', silent_hours_enabled: '夜間安靜'
};

const joinModes: Record<string, string> = {
  off: '不處理', auto_member: '會員自動核准', auto_all: '全部自動核准', manual: '管理員審核'
};

const severityLabel: Record<string, string> = { info: '一般', warning: '注意', error: '錯誤' };

export function AdminSecurity() {
  const { toast } = useApp();
  const [data, setData] = useState<any>(null);
  const [terms, setTerms] = useState<any[]>([]);
  const [admins, setAdmins] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  const [newTerm, setNewTerm] = useState({ term: '', match_type: 'contains', action: 'delete', note: '' });
  const [newAdmin, setNewAdmin] = useState('');

  async function load() {
    try {
      const [security, blocked, adminList] = await Promise.all([api.security(), api.blockedTerms(), api.admins()]);
      setData(security); setTerms(blocked); setAdmins(adminList);
    } catch (err) { toast(err instanceof Error ? err.message : '載入安全資料失敗', 'error'); }
  }
  useEffect(() => { load(); }, []);

  async function addTerm(event: FormEvent) {
    event.preventDefault();
    if (!newTerm.term.trim()) return;
    setBusy('term');
    try { await api.saveBlockedTerm(newTerm); setNewTerm({ term: '', match_type: 'contains', action: 'delete', note: '' }); await load(); toast('已新增封鎖詞'); }
    catch (err) { toast(err instanceof Error ? err.message : '新增失敗', 'error'); }
    finally { setBusy(''); }
  }
  async function toggleTerm(term: any) {
    try { await api.saveBlockedTerm({ ...term, enabled: !term.enabled }); await load(); }
    catch (err) { toast(err instanceof Error ? err.message : '更新失敗', 'error'); }
  }
  async function removeTerm(id: number) {
    if (!window.confirm('刪除這個封鎖詞？')) return;
    try { await api.deleteBlockedTerm(id); await load(); toast('已刪除'); }
    catch (err) { toast(err instanceof Error ? err.message : '刪除失敗', 'error'); }
  }
  async function addAdmin(event: FormEvent) {
    event.preventDefault();
    const value = newAdmin.trim();
    if (!value) return;
    setBusy('admin');
    try {
      await api.addAdmin(/^\d+$/.test(value) ? { telegram_id: value } : { username: value });
      setNewAdmin(''); await load(); toast('已授予後台權限');
    } catch (err) { toast(err instanceof Error ? err.message : '新增失敗', 'error'); }
    finally { setBusy(''); }
  }
  async function removeAdmin(admin: any) {
    if (!window.confirm(`移除 ${admin.first_name || admin.telegram_id} 的後台權限？`)) return;
    try { await api.removeAdmin(admin.id); await load(); toast('已移除'); }
    catch (err) { toast(err instanceof Error ? err.message : '移除失敗', 'error'); }
  }
  async function unmute(userId: number) {
    try { await api.unmuteMember(userId); await load(); toast('已解除禁言'); }
    catch (err) { toast(err instanceof Error ? err.message : '解除失敗', 'error'); }
  }
  async function exportCsv(dataset: string) {
    setBusy(`export-${dataset}`);
    try { await downloadCsv(dataset); toast('匯出完成'); }
    catch (err) { toast(err instanceof Error ? err.message : '匯出失敗', 'error'); }
    finally { setBusy(''); }
  }

  if (!data) return <PageLoading label="載入安全中心" />;
  const guards = data.guards || {};

  return <div className="admin-page page-enter">
    <section className="metric-grid">
      <article className="metric-card"><span><ShieldAlert size={20} /></span><p>近 7 天注意事件</p><strong>{(data.counts?.warning || 0) + (data.counts?.error || 0)}</strong><small>一般事件 {data.counts?.info || 0} 筆</small></article>
      <article className="metric-card"><span><VolumeX size={20} /></span><p>禁言中</p><strong>{data.active_mutes?.length || 0}</strong><small>目前受限成員</small></article>
      <article className="metric-card"><span><Ban size={20} /></span><p>封鎖詞</p><strong>{guards.blocked_terms || 0}</strong><small>啟用中的規則</small></article>
      <article className="metric-card"><span><UserCog size={20} /></span><p>後台管理員</p><strong>{guards.admins || 0}</strong><small>可進入管理後台</small></article>
    </section>

    <section className="admin-grid two-one">
      <article className="admin-panel">
        <div className="panel-heading"><div><p>PROTECTION</p><h2>防護狀態</h2></div><ShieldCheck size={20} /></div>
        <div className="guard-grid">
          {Object.entries(guardLabels).map(([key, label]) => <div key={key} className={guards[key] ? 'guard on' : 'guard'}>
            <span>{guards[key] ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />}</span>
            <div><strong>{label}</strong><small>{guards[key] ? '啟用中' : '未啟用'}</small></div>
          </div>)}
          <div className="guard on"><span><UserCog size={15} /></span><div><strong>入群申請</strong><small>{joinModes[guards.join_request_mode] || '不處理'}</small></div></div>
          <div className={guards.webhook_secret ? 'guard on' : 'guard alert'}><span>{guards.webhook_secret ? <ShieldCheck size={15} /> : <AlertTriangle size={15} />}</span><div><strong>Webhook 驗證</strong><small>{guards.webhook_secret ? 'Secret 已設定' : '尚未設定 Secret'}</small></div></div>
          <div className={guards.dev_bypass ? 'guard alert' : 'guard on'}><span>{guards.dev_bypass ? <AlertTriangle size={15} /> : <ShieldCheck size={15} />}</span><div><strong>登入驗證</strong><small>{guards.dev_bypass ? '開發略過模式開啟中' : '強制 Telegram 簽章'}</small></div></div>
        </div>
        <div className="export-row">
          <span>資料匯出</span>
          {['members', 'orders', 'audit'].map((dataset) => <button key={dataset} className="button ghost" onClick={() => exportCsv(dataset)} disabled={busy === `export-${dataset}`}>
            {busy === `export-${dataset}` ? <Loader2 className="spin" size={15} /> : <Download size={15} />}{({ members: '會員', orders: '訂單', audit: '稽核' } as Record<string, string>)[dataset]}
          </button>)}
        </div>
      </article>

      <article className="admin-panel">
        <div className="panel-heading"><div><p>MUTED</p><h2>禁言中成員</h2></div><VolumeX size={20} /></div>
        {data.active_mutes?.length ? <ul className="simple-list">
          {data.active_mutes.map((mute: any) => <li key={mute.id}>
            <div><strong>{mute.first_name || mute.username || mute.user_id}</strong><small>{mute.reason || '未填原因'} · {mute.expires_at ? `至 ${formatDate(mute.expires_at, true)}` : '永久'}</small></div>
            <button className="button ghost" onClick={() => unmute(mute.user_id)}>解除</button>
          </li>)}
        </ul> : <EmptyState icon={ShieldCheck} title="目前沒有禁言中的成員" body="自動處置或手動禁言都會顯示在這裡。" />}
      </article>
    </section>

    <section className="admin-grid two-one">
      <article className="admin-panel">
        <div className="panel-heading"><div><p>WORD FILTER</p><h2>封鎖詞</h2></div><Ban size={20} /></div>
        <form className="term-form" onSubmit={addTerm}>
          <input value={newTerm.term} onChange={(e) => setNewTerm({ ...newTerm, term: e.target.value })} placeholder="要封鎖的字詞或正則" />
          <select value={newTerm.match_type} onChange={(e) => setNewTerm({ ...newTerm, match_type: e.target.value })}><option value="contains">包含</option><option value="exact">完全符合</option><option value="regex">正則</option></select>
          <select value={newTerm.action} onChange={(e) => setNewTerm({ ...newTerm, action: e.target.value })}><option value="delete">刪除訊息</option><option value="warn">刪除並警告</option><option value="mute">刪除並禁言</option><option value="ban">刪除並封鎖</option></select>
          <button className="button primary" disabled={busy === 'term'}><Plus size={15} />新增</button>
        </form>
        {terms.length ? <div className="data-table-wrap"><table className="data-table"><thead><tr><th>字詞</th><th>比對</th><th>處置</th><th>命中</th><th /></tr></thead><tbody>
          {terms.map((term) => <tr key={term.id} className={term.enabled ? '' : 'muted-row'}>
            <td><strong>{term.term}</strong>{term.note && <small className="cell-note">{term.note}</small>}</td>
            <td>{({ contains: '包含', exact: '完全符合', regex: '正則' } as Record<string, string>)[term.match_type]}</td>
            <td>{({ delete: '刪除', warn: '警告', mute: '禁言', ban: '封鎖' } as Record<string, string>)[term.action]}</td>
            <td>{term.hit_count}</td>
            <td className="row-actions"><button className="text-button" onClick={() => toggleTerm(term)}>{term.enabled ? '停用' : '啟用'}</button><button className="icon-button" onClick={() => removeTerm(term.id)}><Trash2 size={15} /></button></td>
          </tr>)}
        </tbody></table></div> : <EmptyState icon={Ban} title="尚未設定封鎖詞" body="加入常見的廣告或詐騙字詞，Bot 會自動刪除。" />}
      </article>

      <article className="admin-panel">
        <div className="panel-heading"><div><p>ADMINS</p><h2>後台管理員</h2></div><UserCog size={20} /></div>
        <form className="term-form" onSubmit={addAdmin}>
          <input value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} placeholder="@username 或 Telegram ID" />
          <button className="button primary" disabled={busy === 'admin'}><Plus size={15} />授權</button>
        </form>
        <ul className="simple-list">
          {admins.map((admin) => <li key={admin.id}>
            <div><strong>{admin.first_name || admin.username || admin.telegram_id}</strong><small>{admin.locked ? '環境變數指定' : admin.admin_role === 'owner' ? '擁有者' : '管理員'} · 最近登入 {formatDate(admin.last_admin_login_at, true)}</small></div>
            {admin.locked ? <span className="bind-chip">鎖定</span> : <button className="button ghost danger" onClick={() => removeAdmin(admin)}>移除</button>}
          </li>)}
        </ul>
        <p className="hint-text">新管理員必須先私訊 Bot 按下「開始」，系統才找得到這個帳號。</p>
      </article>
    </section>

    <section className="admin-panel">
      <div className="panel-heading"><div><p>SECURITY LOG</p><h2>安全事件</h2></div><ShieldAlert size={20} /></div>
      {data.events?.length ? <div className="data-table-wrap"><table className="data-table"><thead><tr><th>時間</th><th>等級</th><th>事件</th><th>對象</th><th>說明</th></tr></thead><tbody>
        {data.events.map((event: any) => <tr key={event.id}>
          <td>{formatDate(event.created_at, true)}</td>
          <td><span className={`severity ${event.severity}`}>{severityLabel[event.severity] || event.severity}</span></td>
          <td><code>{event.kind}</code></td>
          <td>{event.first_name || event.username || '—'}</td>
          <td>{event.summary}</td>
        </tr>)}
      </tbody></table></div> : <EmptyState icon={ShieldCheck} title="沒有安全事件" body="自動防護觸發時會記錄在這裡。" />}
    </section>
  </div>;
}
